import pdfMake from 'pdfmake';
import handlePurchase from "@/services/modules/purchase";
import pdfFonts from 'pdfmake/build/vfs_fonts';
import figureFormatter from "@/services/utils/figureFormatter";
import handleHospital from "@/services/modules/hospital";
import { formatDate, formatToDdMmYy } from '@/services/utils/global';
pdfMake.vfs = pdfFonts.pdfMake.vfs;

pdfMake.fonts = {
    SulaimanLipi: {
        normal: 'https://fonts.cdnfonts.com/s/14639/solaimanlipi.woff',
        bold: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Medium.ttf',
        italics: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Italic.ttf',
        bolditalics: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-MediumItalic.ttf'
    }
}

const pdfPrinter = () => {

    const { currencyToWord } = handlePurchase();
    const { onlyCommaFormat, commaFormat } = figureFormatter();
    const { calculateAge } = handleHospital();

    const exportToPDF = (company, invoice, barcode, qrcode, barcodePatient, userName, btnTitle, titleByType = false, isSalesMaster = false, isBillType = false, isRedirect = false) => {
        const tableItems = invoice.has_item_detail ? invoice.general : invoice.ledgers;

        var doc = {
            pageSize: 'A4',
            pageMargins: getPageMargin(),
            header: getHeader(company, invoice),
            footer: getFooter(userName, qrcode, invoice, isRedirect),
            content: getContent(invoice, tableItems, barcode, barcodePatient, userName, btnTitle, titleByType, isSalesMaster),
            styles: {
                header: {
                    fontSize: 24,
                }
            },
            defaultStyle: {
                color: 'black',
                fontSize: 10,
                font: 'SulaimanLipi'
            },
            info: {
                title: invoice.bill_number
            }
        }

        pushIntoTable(doc, invoice, tableItems, btnTitle, isBillType)

        const pdfDocGenerator = pdfMake.createPdf(doc);
        pdfDocGenerator.open();
    }

    const getPageMargin = () => {
        return [40, 100, 40, 80];
    }

    function formatTimeOnly(dateTime) {
        if (!dateTime) return '';
        const date = new Date(dateTime);
        return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    }

    const getHeader = (company, invoice) => {
        if (invoice.sale_type === 'opd_bill') {
            return;
        }

        return {
            margin: [40, 25, 40, 10],
            columns: [
                company.logo64
                    ? {
                        alignment: 'left',
                        image: company.logo64,
                        maxHeight: 60
                    }
                    : null,

                {
                    alignment: 'right',
                    stack: [
                        company.name     ? company.name                       : '',
                        company.address  ? 'Address: '  + company.address     : '',
                        company.phone    ? 'Phone: '    + company.phone       : '',
                        company.email    ? 'E-mail: '   + company.email       : '',
                        company.vat_no   ? 'VAT Reg No: ' + company.vat_no   : '',
                    ]
                }
            ]
        }
    }

    const getContent = (invoice, tableItems, barcode, barcodePatient, userName, btnTitle, titleByType = false, isSalesMaster = false) => {
        const getPatientDetails = (patient, fieldName) => {
            if (patient.profile_items) {
                const field = patient.profile_items.find(info => info.field === fieldName)
                return (field && field.value) ?? '';
            }
            return "";
        }

        const getReceiptTable = () => {
            if (invoice.ipd_receipt_masters && invoice.ipd_receipt_masters.length > 0) {
                const hasMemo   = invoice.ipd_receipt_masters.some(item => item.description || '');
                const hasMoPRef = invoice.ipd_receipt_masters.some(item => item.receipt_reference || '');

                const widths = ['*', 'auto', 'auto'];
                if (hasMoPRef) widths.push('auto');
                if (hasMemo)   widths.push('auto');
                widths.push('auto', 'auto');

                return {
                    table: {
                        widths: widths,
                        alignment: 'right',
                        body: getFooterTable(invoice, userName, isSalesMaster, hasMemo, hasMoPRef)
                    },
                    layout: {
                        hLineWidth: function (i, node) { return 0.5; },
                        vLineWidth: function (i, node) { return 0.5; },
                        fillColor: function (rowIndex, node, columnIndex) {
                            return (rowIndex === 0) ? '#f3f2f7' : null;
                        }
                    },
                    margin: [0, 10]
                }
            }
        }

        return [
            {
                text: btnTitle,
                style: 'header',
                alignment: 'center',
                margin: [0, 5, 20, 0],
                bold: true,
                color: 'black',
            },

            {
                margin: [0, 10, 0, 0],
                alignment: 'justify',
                columns: [
                    {
                        width: '50%',
                        stack: [
                            { svg: barcodePatient },

                            {
                                text: `Patient ID: ${invoice.contact_profile &&
                                                     invoice.contact_profile.patient_id ?
                                                     invoice.contact_profile.patient_id : invoice.contact.id}`,
                                alignment: 'left',
                                width: '50%'
                            },

                            {
                                text: `Patient Name: ${invoice.contact.name.toUpperCase()}`,
                                alignment: 'left',
                                width: '50%'
                            },

                            {
                                text: `${invoice.contact.birthday ? ('Age: ' + calculateAge(invoice.contact.birthday, invoice.date) + ' | ' + 'Gender: ' + capitalize(invoice.contact.gender)) : ''}`,
                                alignment: 'left',
                                width: '50%'
                            },

                            {
                                text: `Patient Phone No: ${getPatientDetails(invoice.contact, 'mobile_no')}`,
                                alignment: 'left',
                                width: '50%'
                            },

                            {
                                text: `${invoice.contact.address ? 'Patient Address: ' + invoice.contact.address : ''}`,
                                alignment: 'left',
                                width: '50%'
                            }
                        ]
                    },

                    {
                        alignment: 'right',
                        stack: [
                            { svg: barcode },

                            {
                                text: [
                                    { text: 'BILL DATE: ', bold: true, margin: [0, 0, 10, 0] },
                                    { text: formatToDdMmYy(invoice.date) }
                                ]
                            },

                            {
                                text: [
                                    { text: 'BILL NO: ', bold: true, margin: [0, 0, 10, 0] },
                                    { text: invoice.bill_number }
                                ]
                            },
                        ]
                    },
                ]
            },

            {
                style: 'tableExample',
                margin: [0, 10, 0, 0],
                table: {
                    widths: getTableWidth(btnTitle, tableItems, invoice),
                    body: []
                },
                layout: {
                    fillColor: function (rowIndex, node, columnIndex) {
                        return (rowIndex === 0) ? '#f3f2f7' : null;
                    },
                    hLineWidth: function (i, node) { return 0.5; },
                    vLineWidth: function (i, node) { return 0.5; }
                }
            },

            {
                columns: [
                    {
                        width: '65%',
                        stack: [
                            {
                                text: invoice.total_amount ? ('In Words: ' + currencyToWord(invoice.total_amount)) : ''
                            },
                            {
                                margin: [15, 25],
                                fontSize: 14,
                                table: {
                                    body: [
                                        [{
                                            text: invoice.status !== 'returned' ? capitalize(invoice.payment_status) : capitalize(invoice.status),
                                            margin: [10, 5]
                                        }]
                                    ],
                                },
                                layout: {
                                    fillColor: '#f3f2f7'
                                }
                            },
                        ]
                    },

                    {
                        table: {
                            widths: ['*', '*'],
                            alignment: 'right',
                            body: totalBodyData(invoice)
                        },
                        layout: {
                            hLineWidth: function (i, node) { return 0.5; },
                            vLineWidth: function (i, node) { return 0.5; }
                        }
                    },
                ],
                margin: [0, 10]
            },

            {
                alignment: 'justify',
                text: [{ text: invoice.description ? 'NOTE: \n' : '', bold: true }, { text: invoice.description }]
            },

            getReceiptTable()
        ]
    }

    const getTableWidth = (btnTitle, tableItems, invoice) => {
        const hasRoomNumber = tableItems.some(item =>
            invoice.has_item_detail && item.product && item.product.short_description
        );

        if (btnTitle !== 'BILL') {
            return ['*', 'auto'];
        }

        const rowStructure = ['auto', '*'];
        if (hasRoomNumber) rowStructure.push('auto');
        rowStructure.push('auto', 'auto', 'auto');

        return rowStructure;
    }

    const totalBodyData = (invoice) => {
        const tableBody = [];

        tableBody.push([
            { text: 'Total Amount', alignment: 'right', bold: true },
            { text: commaFormat(invoice.subtotal_amount.toFixed(2)), alignment: 'right' }
        ]);

        if (invoice.service_charge_amount) {
            tableBody.push([
                { text: 'Service Charge', alignment: 'right', bold: true },
                { text: onlyCommaFormat(invoice.service_charge_amount.toFixed(2)), alignment: 'right' }
            ]);
        }

        if (invoice.vat_amount) {
            tableBody.push([
                { text: 'VAT', alignment: 'right', bold: true },
                { text: onlyCommaFormat(invoice.vat_amount.toFixed(2)), alignment: 'right' }
            ]);
        }

        if (totalDiscount(invoice) > 0) {
            tableBody.push([
                { text: 'Discount', alignment: 'right', bold: true },
                { text: commaFormat(totalDiscount(invoice).toFixed(2)), alignment: 'right' }
            ]);
        }

        tableBody.push([
            { text: 'Net Bill Amount', alignment: 'right', bold: true },
            { text: totalNetBillAmount(invoice), alignment: 'right' }
        ]);

        if (getAdjustmentAmount(invoice) > 0) {
            tableBody.push([
                { text: 'Adjust Amount', alignment: 'right', bold: true },
                { text: commaFormat(getAdjustmentAmount(invoice).toFixed(2)), alignment: 'right' }
            ]);
        }

        tableBody.push(
            [{ text: 'Paid Amount', alignment: 'right', bold: true }, { text: commaFormat(invoice.paid_amount.toFixed(2)), alignment: 'right' }],
            [{ text: 'Due', alignment: 'right', bold: true }, { text: totalDue(invoice), alignment: 'right' }]
        );

        return tableBody;
    }

    const totalDue = (invoice) => {
        let totalDiscount = invoice.ipd_receipt_masters.reduce((sum, item) => sum + (item.discount_amount ?? 0), 0);
        let totalDue = invoice.total_amount - (invoice.paid_amount + totalDiscount) - getAdjustmentAmount(invoice);
        return commaFormat(totalDue.toFixed(2));
    }

    const getAdjustmentAmount = (invoice) => {
        return invoice.adjusted_amount - invoice.discount;
    }

    const totalDiscount = (invoice) => {
        let totalDiscount = invoice.ipd_receipt_masters.reduce((sum, item) => sum + (item.discount_amount ?? 0), 0);
        totalDiscount += invoice.total_discount;
        return totalDiscount;
    }

    const totalNetBillAmount = (invoice) => {
        let totalDiscount = invoice.ipd_receipt_masters.reduce((sum, item) => sum + (item.discount_amount ?? 0), 0);
        let totalAmount = invoice.total_amount - totalDiscount;
        return commaFormat(totalAmount.toFixed(2));
    }

    const getFooterTable = (invoice, userName, isSalesMaster = false, hasMemo = false, hasMoPRef = false) => {
        let sale_master = invoice.sale_master ?? undefined;
        if (isSalesMaster) {
            sale_master = invoice;
        }

        const tableHeader = [
            { text: 'MR Date & Time' },
            { text: 'MR No' },
            { text: 'Mode of payment' },
        ];

        if (hasMoPRef) tableHeader.push({ text: 'MoP Ref.' });
        if (hasMemo)   tableHeader.push({ text: 'Memo' });

        tableHeader.push({ text: 'Receipt by' });
        tableHeader.push({ text: 'Receipt Amount', alignment: 'right' });

        return [
            tableHeader,
            ...moneyReceiptTable(sale_master, invoice, hasMemo, hasMoPRef)
        ]
    }

    const moneyReceiptTable = (sale_master, invoice, hasMemo = false, hasMoPRef = false) => {
        return sale_master.ipd_receipt_masters
            .filter(receiptMaster =>
                receiptMaster.total_paid_amount !== 0 ||
                receiptMaster.refund_amount !== 0
            )
            .map(receiptMaster => {
                const paid   = receiptMaster.total_paid_amount ?? 0;
                const refund = receiptMaster.refund_amount ?? 0;
                const amount = paid !== 0 ? paid : refund !== 0 ? -refund : 0;

                const row = [
                    {
                        text: formatToDdMmYy(receiptMaster.receipt_date) +
                              ' ' +
                              formatTimeOnly(receiptMaster.created_at)
                    },
                    { text: receiptMaster.voucher_no ?? '' },
                    { text: (receiptMaster.account_head && receiptMaster.account_head.name) || '' },
                ];

                if (hasMoPRef) row.push({ text: receiptMaster.receipt_reference ?? '' });
                if (hasMemo)   row.push({ text: receiptMaster.description ?? '' });

                row.push({ text: sale_master.receipt_by ?? '' });
                row.push({ text: onlyCommaFormat(amount), alignment: 'right' });

                return row;
            }) ?? [];
    };

    const getFooter = (userName, qrcode, invoice, isRedirect) => {
        if (invoice.sale_type === 'opd_bill') {
            return;
        }

        return {
            margin: [40, 0, 40, 0],
            columns: [
                {
                    columns: [
                        { svg: qrcode },

                        invoice.sale_type === 'pharmacy_bill'
                            ? {
                                width: '90%',
                                alignment: 'left',
                                margin: [5, 20, 0, 0],
                                fontSize: 8,
                                stack: [
                                    {
                                        text: "কাটাছেঁড়া ও মেমো ছাড়া ঔষধ ফেরত যোগ্য নহে। ভবিষ্যৎ অনুসন্ধানের জন্য নিজ দায়িত্বে সংরক্ষণ করুন। মেডিসিন Return/ফেরত এর সময় অবশ্যই ক্যাশ মেমো/Invoice সাথে নিয়ে আসবেন।"
                                    },
                                    {
                                        margin: [0, 10, 0, 0],
                                        text: 'N.B: This is a system generated documents and requires no manual signature.',
                                    }
                                ]
                            }
                            : {
                                width: '90%',
                                alignment: 'left',
                                fontSize: 8,
                                text: isRedirect ? "অনলাইনে রিপোর্ট পেতে QR স্ক্যান করুন" : 'N.B: This is a system generated documents and requires no manual signature.',
                                margin: [5, 53, 0, 0],
                            }
                    ]
                },

                {
                    width: '20%',
                    alignment: 'right',
                    fontSize: 8,
                    margin: [0, 33, 0, 0],
                    stack: [
                        { text: 'Printed by', bold: true },
                        { text: userName },
                        { text: formatDate(new Date()) }
                    ]
                }
            ]
        }
    }

    const capitalize = (text) => {
        if (!text || typeof text !== 'string') {
            return '';
        }
        return text.charAt(0).toUpperCase() + text.slice(1);
    }

    const pushIntoTable = (doc, invoice, tableItems, btnTitle, isBillType) => {
        var flag   = false;
        var isBill = false;

        const hasRoomNumber = tableItems.some(item =>
            invoice.has_item_detail && item.product && item.product.short_description
        );

        tableItems.forEach((item, index) => {
            if (!flag) {
                const tableHeader = getTableHeader(btnTitle, hasRoomNumber);
                doc.content[2].table.body.push(tableHeader);
                flag = true;
            }
            if (!isBill) {
                const rowData = getRowData(invoice, item, btnTitle, index, hasRoomNumber);
                isBill = isBillType;
                return doc.content[2].table.body.push(rowData);
            }
        });
    }

    const getTableHeader = (btnTitle, hasRoomNumber) => {
        if (btnTitle !== 'BILL') {
            return [
                { text: 'Particulars' },
                { text: 'Amount', alignment: 'right' }
            ];
        }

        const header = [
            { text: 'SL' },
            { text: 'Particulars' },
        ];
        if (hasRoomNumber) header.push({ text: 'Room Number' });
        header.push(
            { text: 'Qty',        alignment: 'right' },
            { text: 'Unit Price', alignment: 'right' },
            { text: 'Amount',     alignment: 'right' }
        );

        return header;
    }

    const getRowData = (invoice, item, btnTitle, index, hasRoomNumber) => {

        if (btnTitle !== 'BILL') {
            return [
                { text: invoice.has_item_detail ? item.product.name : item.head.name },
                { text: onlyCommaFormat(item.amount), alignment: 'right' }
            ];
        }

        const row = [
            { text: index + 1 },
            { text: invoice.has_item_detail ? item.product.name : item.head.name },
        ];
        if (hasRoomNumber) {
            row.push({ text: invoice.has_item_detail ? item.product.short_description : '' });
        }
        row.push(
            { text: item.quantity ? onlyCommaFormat(item.quantity) : '', alignment: 'right' },
            { text: item.is_pending_line ? '' : onlyCommaFormat(invoice.has_item_detail ? item.rate : 0),  alignment: 'right' },
            { text: item.is_pending_line ? '' : onlyCommaFormat(item.total_amount),                         alignment: 'right' }
        );

        return row;
    }

    return {
        exportToPDF
    }
}

export default pdfPrinter;